import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { useUserServiceCreateAgencyMutation } from 'zma-react-sdk/user-service'
import { useToast } from '@/hooks/use-toast'
import { useTranslation } from '@/hooks/use-translation'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PhoneNumberInput } from '@/components/phone-number-input'
import {
  Agency,
  createAgencySchema,
  updateAgencySchema,
  CreateAgencyInput,
  UpdateAgencyInput,
} from './schema'

interface AgencyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  agency?: Agency | null
  onSuccess?: () => void
}

export function AgencyDialog({
  open,
  onOpenChange,
  agency,
  onSuccess,
}: AgencyDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const isEditing = !!agency

  // Use appropriate schema based on mode
  const schema = isEditing ? updateAgencySchema : createAgencySchema

  const form = useForm<CreateAgencyInput | UpdateAgencyInput>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      prefix: '+84',
      password: '',
      status: 'Active' as const,
    } as CreateAgencyInput,
  })

  // Reset form when agency changes
  useEffect(() => {
    if (agency && isEditing) {
      form.reset({
        firstName: agency.firstName || '',
        lastName: agency.lastName || '',
        email: agency.email || '',
        phoneNumber: agency.phoneNumber || '',
        prefix: '+84',
        status: agency.status as any,
      } as UpdateAgencyInput)
    } else {
      form.reset({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        prefix: '+84',
        password: '',
        status: 'Active',
      } as CreateAgencyInput)
    }
  }, [agency, isEditing, form])

  const [createMutation, { loading: isLoading }] =
    useUserServiceCreateAgencyMutation({
      onCompleted: () => {
        toast({
          title: t('agencies.createSuccess'),
          description: t('agencies.createSuccessDescription'),
        })
        onSuccess?.()
        onOpenChange(false)
        form.reset()
      },
      onError: (error) => {
        toast({
          variant: 'destructive',
          title: t('agencies.createError'),
          description: error?.message || t('agencies.unknownError'),
        })
      },
      refetchQueries: ['UserServiceUsersByType'],
    })

  // Note: Update functionality would require a separate mutation
  // For now, we'll only support creation as per the original implementation

  const onSubmit = (data: CreateAgencyInput | UpdateAgencyInput) => {
    if (isEditing) {
      // Update functionality would go here
      // For now, show a message that editing is not yet supported
      toast({
        title: t('agencies.editNotSupported'),
        description: t('agencies.editNotSupportedDescription'),
      })
      return
    }

    // Create new agency
    createMutation({
      variables: {
        input: data as CreateAgencyInput,
      },
    })
  }

  const handleClose = () => {
    onOpenChange(false)
    form.reset()
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='max-w-lg'>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('agencies.editAgency') : t('agencies.createAgency')}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? t('agencies.editAgencyDescription')
              : t('agencies.createAgencyDescription')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              <FormField
                control={form.control}
                name='firstName'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('agencies.firstName')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('agencies.firstNamePlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='lastName'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('agencies.lastName')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('agencies.lastNamePlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('agencies.email')}</FormLabel>
                  <FormControl>
                    <Input
                      type='email'
                      placeholder={t('agencies.emailPlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormItem>
              <FormLabel>{t('agencies.phoneNumber')}</FormLabel>
              <FormControl>
                <PhoneNumberInput
                  value={{
                    prefix: form.watch('prefix') || '+84',
                    number: form.watch('phoneNumber') || '',
                  }}
                  onChange={(value) => {
                    form.setValue('prefix', value.prefix)
                    form.setValue('phoneNumber', value.number)
                  }}
                  placeholder={t('agencies.phoneNumberPlaceholder')}
                />
              </FormControl>
              {(form.formState.errors.phoneNumber ||
                form.formState.errors.prefix) && (
                <p className='text-sm font-medium text-destructive'>
                  {form.formState.errors.phoneNumber?.message ||
                    form.formState.errors.prefix?.message}
                </p>
              )}
            </FormItem>

            {!isEditing && (
              <FormField
                control={form.control}
                name='password'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('agencies.password')}</FormLabel>
                    <FormControl>
                      <Input
                        type='password'
                        placeholder={t('agencies.passwordPlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {isEditing && (
              <FormField
                control={form.control}
                name='status'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('common.status')}</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t('agencies.selectStatus')}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value='Active'>
                          {t('agencies.status.active')}
                        </SelectItem>
                        <SelectItem value='Suspended'>
                          {t('agencies.status.suspended')}
                        </SelectItem>
                        <SelectItem value='Pending'>
                          {t('agencies.status.pending')}
                        </SelectItem>
                        <SelectItem value='Disabled'>
                          {t('agencies.status.disabled')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <DialogFooter>
              <Button type='button' variant='outline' onClick={handleClose}>
                {t('common.cancel')}
              </Button>
              <Button type='submit' disabled={isLoading}>
                {isLoading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                {isEditing ? t('common.update') : t('common.create')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
